// ONB4 slice 1 (docs/build-briefs/ONB4-S1-setup-wizard-frame.md; docs/design/onb4-setup-wizard.md). The pure rules
// behind the first sign-in setup wizard, kept out of setupWizard.ts so they can be tested without a browser.
//
// There is no wizard state of its own. Every step's state is computed from the ONB2 setup checklist reads (the same
// ids as admin.ts SETUP_CHECKLIST_STEPS) plus the address read from the same settings answer. "Skipped" is the one
// thing remembered, and only while the wizard is open. ONB4 slice 3 (docs/build-briefs/ONB4-S3-wizard-card-steps.md)
// adds the card steps 5 to 7 and the finish screen.

import { formatCutoffTwelveHour } from "./businessDayConfirm.ts";
import {
  ONE_MORE_THING,
  REVIEWING_LINE,
  type CardPaymentsKind,
  type RequirementItemDto,
  type ReviewState,
} from "./cardPaymentsModel.ts";
import { US_STATE_NAMES } from "./softwareOwnerLegalModel.ts";

export type WizardStepId =
  "bar" | "money" | "drinks" | "people" | "cardPayments" | "reader" | "testSale" | "royalty";

/** The ONB2 checklist ids the wizard reads. Mirrors admin.ts SetupChecklistStepId; test/ui-setup-wizard-model.test.ts
 * holds the two lists equal. */
export const CHECKLIST_ITEM_IDS = [
  "itemKinds",
  "pourVolumes",
  "liquorCategories",
  "bottles",
  "startingStock",
  "recipes",
  "drinkCategories",
  "pricing",
  "memberPrice",
  "taxRate",
  "businessDay",
  "holdAmount",
  "terminals",
  "devices",
  "users",
  "vendors",
] as const;
export type ChecklistItemId = (typeof CHECKLIST_ITEM_IDS)[number];
export type ChecklistState = "done" | "pending" | "unknown";
export type ChecklistStates = Readonly<Partial<Record<string, ChecklistState>>>;

export interface WizardStepDef {
  id: WizardStepId;
  label: string;
  /** The checklist items this step covers. Empty for the card steps, which read their own state (CardReads). */
  items: readonly ChecklistItemId[];
}

export const WIZARD_STEPS: readonly WizardStepDef[] = [
  { id: "bar", label: "Your location", items: ["businessDay"] },
  { id: "money", label: "Money", items: ["taxRate", "holdAmount", "pricing", "memberPrice"] },
  {
    id: "drinks",
    label: "Drinks",
    items: [
      "itemKinds",
      "pourVolumes",
      "liquorCategories",
      "bottles",
      "startingStock",
      "recipes",
      "drinkCategories",
    ],
  },
  { id: "people", label: "People", items: ["users"] },
  { id: "cardPayments", label: "Card payments", items: [] },
  { id: "reader", label: "Card reader", items: [] },
  { id: "testSale", label: "Test sale", items: [] },
];

export const WIZARD_STEP_COUNT = WIZARD_STEPS.length;

/** PAY2-S6 (DEC-406). The eighth step, offered only to a franchise location with a royalty rate in force whose
 * franchisor account is On. The server decides that (GET royalty-authorization `applies`); the wizard never infers it. */
export const ROYALTY_STEP: WizardStepDef = { id: "royalty", label: "Royalty payments", items: [] };

/** The steps this wizard shows: the seven always, and Royalty payments after them when the server offers it. */
export function wizardSteps(royaltyOffered: boolean): readonly WizardStepDef[] {
  return royaltyOffered ? [...WIZARD_STEPS, ROYALTY_STEP] : WIZARD_STEPS;
}

export type WizardStepState = "done" | "pending";

/** What steps 5 to 7 read. Undefined until the card reads answer, so a step is never shown done on a guess. */
export interface CardReads {
  paymentsOn: boolean;
  /** This location has an active card reader. */
  readerRegistered: boolean;
  /** A test sale was approved and refunded in this wizard session. Not stored anywhere. */
  testSaleApproved: boolean;
}

/** What the Royalty payments step reads. Undefined until the royalty read answers (or when it cannot be made). */
export interface RoyaltyReads {
  /** The server offers the step. */
  offered: boolean;
  /** An authorization is active. */
  active: boolean;
}

export interface WizardReads {
  states: ChecklistStates;
  royalty?: RoyaltyReads | undefined;
  /** Street, city, state and ZIP all filled, from the settings answer. Undefined until that answer arrives. */
  addressComplete: boolean | undefined;
  card?: CardReads | undefined;
}

/** A step is done only when every item it covers reads done. An item that is pending or unknown (a read the person
 * may not make) keeps the step open, so the wizard never says more is finished than is known. */
export function stepState(step: WizardStepDef, reads: WizardReads): WizardStepState {
  if (step.id === "cardPayments") return reads.card?.paymentsOn === true ? "done" : "pending";
  if (step.id === "reader") return reads.card?.readerRegistered === true ? "done" : "pending";
  if (step.id === "testSale") return reads.card?.testSaleApproved === true ? "done" : "pending";
  if (step.id === "royalty") return reads.royalty?.active === true ? "done" : "pending";
  const itemsDone = step.items.every((item) => reads.states[item] === "done");
  if (step.id === "bar") return itemsDone && reads.addressComplete === true ? "done" : "pending";
  return itemsDone ? "done" : "pending";
}

export function allStepStates(reads: WizardReads): WizardStepState[] {
  return wizardSteps(reads.royalty?.offered === true).map((step) => stepState(step, reads));
}

/** How many of the 16 checklist items read done. */
export function doneItemCount(states: ChecklistStates): number {
  return CHECKLIST_ITEM_IDS.filter((id) => states[id] === "done").length;
}

/** The step the wizard opens on: the first one that still has a pending item; the last step when none has. */
export function firstPendingIndex(states: readonly WizardStepState[]): number {
  const index = states.findIndex((state) => state !== "done");
  return index === -1 ? states.length - 1 : index;
}

/** Continue: the next step after this one that is not done. Undefined after the last. */
export function nextIndex(current: number, states: readonly WizardStepState[]): number | undefined {
  for (let i = current + 1; i < states.length; i += 1) {
    if (states[i] !== "done") return i;
  }
  return undefined;
}

/** Back is always one tap and always one step. Undefined on the first. */
export function previousIndex(current: number): number | undefined {
  return current > 0 ? current - 1 : undefined;
}

export type RailState = "current" | "done" | "skipped" | "pending";

export function railState(
  index: number,
  current: number,
  states: readonly WizardStepState[],
  skipped: ReadonlySet<WizardStepId>,
  steps: readonly WizardStepDef[] = WIZARD_STEPS,
): RailState {
  if (index === current) return "current";
  if (states[index] === "done") return "done";
  const step = steps[index];
  if (step !== undefined && skipped.has(step.id)) return "skipped";
  return "pending";
}

export const RAIL_STATE_LABEL: Readonly<Record<RailState, string>> = {
  current: "",
  done: "Done",
  skipped: "Skipped",
  pending: "",
};

// ---------------------------------------------------------------------------------------------------
// Step 1: Your location
// ---------------------------------------------------------------------------------------------------

export interface LocationDraft {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  /** "HH:MM", or "" while the placeholder shows. */
  closingHour: string;
  /** IANA zone, or "" while the placeholder shows. */
  timeZone: string;
}

export interface LocationFormView {
  /** The primary is dimmed while this is non-empty; the text is the one reason shown beside it. */
  blockedReason: string;
  /** The Confirm tap beside the time zone. Hidden once confirmed. */
  confirmVisible: boolean;
  confirmEnabled: boolean;
}

export const LOCATION_NEEDS_ADDRESS = "Enter the street address, city, state and ZIP";
export const LOCATION_NEEDS_CLOSING_HOUR = "Choose a closing hour";
export const LOCATION_NEEDS_TIME_ZONE = "Choose a time zone";
export const LOCATION_NEEDS_CONFIRM = "Confirm the time zone to continue";

export function addressComplete(
  draft: Pick<LocationDraft, "street" | "city" | "state" | "postalCode">,
): boolean {
  return (
    draft.street.trim().length > 0 &&
    draft.city.trim().length > 0 &&
    draft.state.length > 0 &&
    draft.postalCode.trim().length > 0
  );
}

export function locationFormView(
  draft: LocationDraft,
  businessDayConfirmed: boolean,
): LocationFormView {
  const pickReady = draft.timeZone.length > 0 && draft.closingHour.length > 0;
  let blockedReason = "";
  if (!addressComplete(draft)) blockedReason = LOCATION_NEEDS_ADDRESS;
  else if (!businessDayConfirmed) {
    blockedReason =
      draft.closingHour.length === 0
        ? LOCATION_NEEDS_CLOSING_HOUR
        : draft.timeZone.length === 0
          ? LOCATION_NEEDS_TIME_ZONE
          : LOCATION_NEEDS_CONFIRM;
  }
  return {
    blockedReason,
    confirmVisible: !businessDayConfirmed,
    confirmEnabled: !businessDayConfirmed && pickReady,
  };
}

/** "03:00" reads "3:00 AM", midnight reads "Midnight". */
export function closingHourLabel(value: string): string {
  return value === "00:00" ? "Midnight" : formatCutoffTwelveHour(value);
}

/** The closing hour picker: the 24 whole hours. A stored value off the hour is added so it is never lost. */
export function closingHourChoices(stored?: string): Array<{ value: string; label: string }> {
  const out: Array<{ value: string; label: string }> = [];
  for (let hour = 0; hour < 24; hour += 1) {
    const value = `${String(hour).padStart(2, "0")}:00`;
    out.push({ value, label: closingHourLabel(value) });
  }
  if (
    stored !== undefined &&
    /^\d{2}:\d{2}$/.test(stored) &&
    !out.some((c) => c.value === stored)
  ) {
    out.push({ value: stored, label: closingHourLabel(stored) });
    out.sort((a, b) => a.value.localeCompare(b.value));
  }
  return out;
}

/** "Eastern Time" for "America/New_York", from the browser's own zone data; the IANA name when Intl gives nothing. */
export function zoneFriendlyName(zone: string): string {
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "longGeneric" })
      .formatToParts(new Date())
      .find((p) => p.type === "timeZoneName");
    if (part !== undefined && part.value.trim() !== "") return part.value;
  } catch (error) {
    if (!(error instanceof RangeError)) throw error;
  }
  return zone;
}

/** "Confirm Eastern" for "America/New_York". */
export function confirmZoneLabel(zone: string): string {
  return `Confirm ${zoneFriendlyName(zone).replace(/ Time$/, "")}`;
}

/** The United States zones the browser lists, as picker choices ("Eastern Time, New York"), plus UTC and the stored
 * zone when it is not among them. Never a free-text field. */
export function timeZoneChoices(
  zones: readonly string[],
  usZones: ReadonlySet<string>,
  stored?: string,
): Array<{ value: string; label: string }> {
  const all = new Set(zones.filter((zone) => usZones.has(zone)));
  all.add("UTC");
  if (stored !== undefined && stored !== "") all.add(stored);
  return [...all]
    .map((zone) => ({ value: zone, label: zoneChoiceLabel(zone) }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

function zoneChoiceLabel(zone: string): string {
  if (zone === "UTC") return "UTC";
  const city = zone.split("/").slice(-1)[0]?.replace(/_/g, " ") ?? zone;
  return `${zoneFriendlyName(zone)}, ${city}`;
}

/** The zone the picker opens on: the one already confirmed; otherwise the browser's own zone when it is a US zone
 * (shown, editable and confirmed by a tap, per the mockup); otherwise nothing, so the person chooses. */
export function initialTimeZone(options: {
  confirmed: boolean;
  stored: string;
  browserZone: string | undefined;
  usZones: ReadonlySet<string>;
}): string {
  if (options.confirmed) return options.stored;
  if (options.browserZone !== undefined && options.usZones.has(options.browserZone)) {
    return options.browserZone;
  }
  return "";
}

export function stateChoices(): Array<{ value: string; label: string }> {
  return Object.entries(US_STATE_NAMES)
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

// ---------------------------------------------------------------------------------------------------
// Step 2: Money
// ---------------------------------------------------------------------------------------------------

/** The card hold presets, in cents. The hold amount is a chosen preset, never typed. */
export const HOLD_PRESET_CENTS: readonly number[] = [2500, 5000, 7500, 10000];

export const MONEY_NEEDS_TAX_CONFIRM = "Confirm the tax rate to continue";

export function moneyBlockedReason(taxConfirmed: boolean): string {
  return taxConfirmed ? "" : MONEY_NEEDS_TAX_CONFIRM;
}

/** "7.25%" from basis points. */
export function taxRateText(basisPoints: number): string {
  const percent = basisPoints / 100;
  return `${Number.isInteger(percent) ? percent.toFixed(0) : percent.toFixed(2).replace(/0$/, "")}%`;
}

// ---------------------------------------------------------------------------------------------------
// Step 4: People
// ---------------------------------------------------------------------------------------------------

export interface PersonDraft {
  name: string;
  role: string;
  pin: string;
}

export interface PersonFormView {
  canAdd: boolean;
  /** The one reason Add person is dimmed; "" when it is live or nothing has been started. */
  blockedReason: string;
}

export function personFormView(draft: PersonDraft, minLength: number): PersonFormView {
  const name = draft.name.trim();
  const started = name.length > 0 || draft.role.length > 0 || draft.pin.length > 0;
  let reason = "";
  if (name.length === 0) reason = "Enter a name";
  else if (draft.role.length === 0) reason = "Choose a role";
  else if (draft.pin.length < minLength) reason = "Enter a PIN";
  return { canAdd: reason === "", blockedReason: started ? reason : "" };
}

export function pinAppend(pin: string, digit: string, maxLength: number): string {
  if (!/^\d$/.test(digit) || pin.length >= maxLength) return pin;
  return pin + digit;
}

export function pinBackspace(pin: string): string {
  return pin.slice(0, -1);
}

/** The dots above the pad: at least the minimum length, more as digits go in, up to the maximum. */
export function pinDotCount(pin: string, minLength: number, maxLength: number): number {
  return Math.min(maxLength, Math.max(minLength, pin.length));
}

// ---------------------------------------------------------------------------------------------------
// Step 5: Card payments (docs/build-briefs/ONB4-S3-wizard-card-steps.md; mockups onb4-4-*)
// ---------------------------------------------------------------------------------------------------

/** The four states of step 5. */
export type CardPaymentsPhase = "ready" | "waiting" | "needs" | "reviewing" | "on";

/** Which of the four states the location's card payments read puts step 5 in. A location the read cannot describe (the feature is
 * off for the deployment, or the person may not look) reads as waiting: the step never invents an account. */
export function cardPaymentsPhase(input: {
  kind: CardPaymentsKind | undefined;
  /** The browser came back from Stripe in this wizard session. */
  arrived: boolean;
  /** STRIPETAB1: the server's last return read, by the model's reviewStateOf. */
  review?: ReviewState;
}): CardPaymentsPhase {
  if (input.review === "reviewing") return "reviewing";
  if (input.review === "more" && (input.kind === "not_started" || input.kind === "in_progress")) return "needs";
  switch (input.kind) {
    case undefined:
    case "no_legal_entity":
    case "not_set_up":
    case "creating":
      return "waiting";
    case "enabled":
      return "on";
    case "restricted":
    case "disconnected":
      return "needs";
    case "not_started":
    case "in_progress":
      // A created account always has things Stripe still needs, so that alone is not "back from Stripe".
      return input.arrived ? "needs" : "ready";
  }
}

export interface CardStepRow {
  label: string;
  value: string;
  tag?: { label: string; ok: boolean };
}

export interface CardPaymentsStepView {
  title: string;
  tag: { label: string; ok: boolean };
  rows: CardStepRow[];
  primaryLabel: string;
  /** Whether the primary may be tapped. A dimmed primary here has no reason beside it: the card says it. */
  primaryEnabled: boolean;
  /** What the primary does. */
  primaryAction: "stripe" | "next";
  checkAgain: boolean;
  skip: boolean;
}

/** "Bank account, owner ID": the labels of what Stripe still needs, in the order the server sent them. */
export function stillNeededText(requirements: readonly RequirementItemDto[]): string {
  return requirements
    .filter((requirement) => requirement.pending)
    .map((requirement) => requirement.label)
    .join(", ");
}

export function cardPaymentsStepView(input: {
  phase: CardPaymentsPhase;
  requirements: readonly RequirementItemDto[];
  reasonLabel: string | null;
  masked: string | null;
  /** The session holds admin.payments.onboard. */
  canOnboard: boolean;
  /** STRIPETAB1 */
  review?: ReviewState;
}): CardPaymentsStepView {
  const { phase } = input;
  if (phase === "reviewing") {
    // Stripe is working: no red, no list of what the owner just did, no Continue on Stripe. The page updates on its own.
    return {
      title: "Turn on card payments",
      tag: { label: "Reviewing", ok: false },
      rows: [{ label: "Charges", value: REVIEWING_LINE }],
      primaryLabel: "Go to the next step",
      primaryEnabled: true,
      primaryAction: "next",
      checkAgain: false,
      skip: true,
    };
  }
  if (phase === "on") {
    return {
      title: "Card payments are on",
      tag: { label: "On", ok: true },
      rows: [
        {
          label: "Charges",
          value: input.masked === null ? "On" : `On, Stripe account ${input.masked}`,
          tag: { label: "Done", ok: true },
        },
      ],
      primaryLabel: "Continue",
      primaryEnabled: true,
      primaryAction: "next",
      checkAgain: false,
      skip: false,
    };
  }
  if (phase === "waiting") {
    return {
      title: "Turn on card payments",
      tag: { label: "Waiting on Snip & Sip", ok: false },
      rows: [
        {
          label: "Stripe account",
          value: "Not created yet",
          tag: { label: "Waiting", ok: false },
        },
        { label: "Next", value: "Your Stripe account is created automatically. Stripe asks for the bank account and owner ID." },
      ],
      primaryLabel: "Continue on Stripe",
      primaryEnabled: false,
      primaryAction: "stripe",
      checkAgain: true,
      skip: true,
    };
  }
  if (phase === "needs") {
    const needed = stillNeededText(input.requirements);
    const still = needed !== "" ? needed : (input.reasonLabel ?? "");
    const rows: CardStepRow[] = [
      { label: "Charges", value: "Not on yet", tag: { label: "Needs more", ok: false } },
    ];
    if (still !== "") rows.push({ label: "Still needed", value: still });
    const more = input.review === "more";
    return {
      title: more ? ONE_MORE_THING : "Back from Stripe",
      tag: { label: more ? "One more thing" : "Stripe needs more", ok: false },
      rows,
      primaryLabel: "Back to Stripe",
      primaryEnabled: input.canOnboard,
      primaryAction: "stripe",
      checkAgain: true,
      skip: false,
    };
  }
  return {
    title: "Turn on card payments",
    tag: { label: "Ready for you", ok: true },
    rows: [
      {
        label: "Stripe account",
        value: "Created by Snip & Sip",
        tag: { label: "Ready", ok: true },
      },
      { label: "Have ready", value: "Owner ID, bank account, business tax ID" },
    ],
    primaryLabel: "Continue on Stripe",
    primaryEnabled: input.canOnboard,
    primaryAction: "stripe",
    checkAgain: false,
    skip: false,
  };
}

/** Steps 6 and 7 are dimmed with this reason until the account reads on. */
export const CARD_NOT_ON_YET = "Card payments are not on yet";

// ---------------------------------------------------------------------------------------------------
// Step 6: Card reader (mockups onb4-5-*)
// ---------------------------------------------------------------------------------------------------

/** The reader name chips, as the mockup shows them. Other opens a text field. */
export const READER_NAME_CHIPS: readonly string[] = ["Front bar", "Patio", "Back bar", "Other"];
export const READER_OTHER_CHIP = "Other";

export interface ReaderDraft {
  /** One of READER_NAME_CHIPS, or "" while none is chosen. */
  chip: string;
  /** The typed name when the chip is Other. */
  other: string;
  code: string;
}

export function readerName(draft: Pick<ReaderDraft, "chip" | "other">): string {
  return draft.chip === READER_OTHER_CHIP ? draft.other.trim() : draft.chip;
}

/** The pairing code as the reader shows it, lowercase with no spaces around it. */
export function readerCodeOf(code: string): string {
  return code.trim().toLowerCase();
}

export interface ReaderFormView {
  canPair: boolean;
  /** Use a test reader: a name is all it needs. */
  canUseTest: boolean;
  /** The one reason Pair reader is dimmed; "" when it is live. */
  blockedReason: string;
}

export function readerFormView(draft: ReaderDraft, cardPaymentsOn: boolean): ReaderFormView {
  const named = readerName(draft) !== "";
  const coded = readerCodeOf(draft.code) !== "";
  let reason = "";
  if (!cardPaymentsOn) reason = CARD_NOT_ON_YET;
  else if (!named)
    reason = draft.chip === READER_OTHER_CHIP ? "Enter a reader name" : "Choose a reader name";
  else if (!coded) reason = "Enter the reader code";
  return {
    canPair: reason === "",
    canUseTest: cardPaymentsOn && named,
    blockedReason: reason,
  };
}

/** The tag beside a paired reader, from the provider's own report. */
export function readerConnectionTag(connection: "online" | "offline" | "unknown" | undefined): {
  label: string;
  ok: boolean;
} {
  if (connection === "online") return { label: "Online", ok: true };
  if (connection === "offline") return { label: "Offline", ok: false };
  return { label: "Checking", ok: false };
}

// ---------------------------------------------------------------------------------------------------
// Step 7: Test sale (docs/build-briefs/ONB4-S2-test-sale.md statuses)
// ---------------------------------------------------------------------------------------------------

export type TestSaleStatus =
  | "waiting"
  | "approved_refunded"
  | "declined"
  | "timed_out"
  | "cancelled"
  | "refund_pending"
  | "refund_failed";

export interface TestSaleOutcome {
  label: string;
  ok: boolean;
  /** Counts as done: only an approved sale that was refunded. */
  done: boolean;
  /** The poll keeps asking: the sale is still on the reader, or the refund is not confirmed. */
  polling: boolean;
  /** Try again starts a new sale. Never offered while money may still be out. */
  canRetry: boolean;
}

export function testSaleOutcome(status: TestSaleStatus, live = false): TestSaleOutcome {
  switch (status) {
    case "waiting":
      return { label: "Waiting", ok: false, done: false, polling: true, canRetry: false };
    case "approved_refunded":
      // LIVETEST1 (DEC-600): a live sale reads Refunded, the last of its three plain states (Charged $1.00, Refunded, done).
      return { label: live ? "Refunded" : "Approved", ok: true, done: true, polling: false, canRetry: false };
    case "declined":
      return { label: "Declined", ok: false, done: false, polling: false, canRetry: true };
    case "timed_out":
      return { label: "Timed out", ok: false, done: false, polling: false, canRetry: true };
    case "cancelled":
      return { label: "Cancelled", ok: false, done: false, polling: false, canRetry: true };
    case "refund_pending":
      return { label: live ? "Charged $1.00" : "Refund pending", ok: false, done: false, polling: true, canRetry: false };
    case "refund_failed":
      return { label: "Refund failed", ok: false, done: false, polling: false, canRetry: false };
  }
}

/** LIVETEST1 (DEC-600). What the step says before Start, in plain words. A live location is told it is a real charge. */
export function testSaleIntro(live: boolean): string {
  return live
    ? "This is a real $1.00 charge on the card you tap. We refund it right away."
    : "Tap any card on the reader. We refund it right away.";
}

export function testSaleNotice(live: boolean): string {
  return live
    ? "Press Start, then tap a card on the reader. The $1.00 never shows in your reports."
    : "This sale will not show in your reports.";
}

/** How often the wizard asks where the sale stands, in milliseconds. */
export const TEST_SALE_POLL_MS = 2000;

// ---------------------------------------------------------------------------------------------------
// Finish screen (mockups onb4-6-*)
// ---------------------------------------------------------------------------------------------------

export interface FinishRow {
  id: "cardPayments" | "reader" | "testSale" | "royalty" | "setup";
  label: string;
  value: string;
  ok: boolean;
  tag?: { label: string; ok: boolean };
  /** Continue returns to the step; See what is left opens the checklist. */
  action?: "continue" | "left";
}

export interface FinishView {
  eyebrow: string;
  title: string;
  /** Every card step is done. */
  allDone: boolean;
  rows: FinishRow[];
}

export function finishView(input: {
  locationName: string | undefined;
  paymentsOn: boolean;
  readerName: string | undefined;
  readerConnection: "online" | "offline" | "unknown" | undefined;
  testSaleApproved: boolean;
  doneItems: number;
  totalItems: number;
  /** PAY2-S6. Present only when the server offers Royalty payments. */
  royalty?: { active: boolean; bankLast4: string | null } | undefined;
}): FinishView {
  const locationName = input.locationName ?? "This location";
  const readerPaired = input.readerName !== undefined;
  const allDone = input.paymentsOn && readerPaired && input.testSaleApproved;
  const rows: FinishRow[] = [
    input.paymentsOn
      ? { id: "cardPayments", label: "Card payments", value: "On", ok: true }
      : {
          id: "cardPayments",
          label: "Card payments",
          value: "Not on yet",
          ok: false,
          action: "continue",
        },
    readerPaired
      ? {
          id: "reader",
          label: "Card reader",
          value: input.readerName ?? "",
          ok: true,
          tag: readerConnectionTag(input.readerConnection),
        }
      : {
          id: "reader",
          label: "Card reader",
          value: "Not paired",
          ok: false,
          ...(input.paymentsOn ? { action: "continue" as const } : {}),
        },
    input.testSaleApproved
      ? {
          id: "testSale",
          label: "Test sale",
          value: "$1.00",
          ok: true,
          tag: { label: "Approved", ok: true },
        }
      : {
          id: "testSale",
          label: "Test sale",
          value: "Not run",
          ok: false,
          ...(input.paymentsOn && readerPaired ? { action: "continue" as const } : {}),
        },
    ...(input.royalty === undefined
      ? []
      : [
          input.royalty.active
            ? {
                id: "royalty" as const,
                label: "Royalty payments",
                value:
                  input.royalty.bankLast4 === null
                    ? "On"
                    : `On, account ending ${input.royalty.bankLast4}`,
                ok: true,
              }
            : {
                id: "royalty" as const,
                label: "Royalty payments",
                value: "To do",
                ok: false,
                action: "continue" as const,
              },
        ]),
    {
      id: "setup",
      label: "Setup",
      value: `${input.doneItems} of ${input.totalItems} done`,
      ok: true,
      action: "left",
    },
  ];
  return {
    eyebrow: allDone ? "All set" : "Left to do",
    title: allDone
      ? `${locationName} can take card payments`
      : `${locationName} cannot take card payments yet`,
    allDone,
    rows,
  };
}

/** The first card step that is not done, for a Continue on the finish screen. Undefined when all three are done. */
export function firstUnfinishedCardStep(card: CardReads | undefined): WizardStepId | undefined {
  if (card?.paymentsOn !== true) return "cardPayments";
  if (!card.readerRegistered) return "reader";
  if (!card.testSaleApproved) return "testSale";
  return undefined;
}

// ---------------------------------------------------------------------------------------------------
// Coming back from Stripe
// ---------------------------------------------------------------------------------------------------

/** Set (in sessionStorage, for this tab only) when Continue on Stripe is tapped from the wizard, so the return Stripe
 * sends to the app root (`?card_payments=return`, the same URL Settings gets) reopens the wizard on step 5 instead of
 * Settings. It carries nothing but the fact. Settings clears it when it sends the browser to Stripe itself. */
export const STRIPE_RETURN_MARKER_KEY = "bms-setup-wizard-stripe";

type MarkerStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export function setStripeReturnMarker(storage: MarkerStorage): void {
  try {
    storage.setItem(STRIPE_RETURN_MARKER_KEY, "1");
  } catch {
    // Storage refused: the return falls back to Settings, which reads the same account.
  }
}

export function clearStripeReturnMarker(storage: MarkerStorage): void {
  try {
    storage.removeItem(STRIPE_RETURN_MARKER_KEY);
  } catch {
    // Nothing to clear.
  }
}

/** True once for a marker that was set; the marker is gone after this call either way. */
export function takeStripeReturnMarker(storage: MarkerStorage): boolean {
  try {
    const set = storage.getItem(STRIPE_RETURN_MARKER_KEY) === "1";
    clearStripeReturnMarker(storage);
    return set;
  } catch {
    return false;
  }
}
